// The workspace BRAND — the colours a client keeps, the Course-wide colours every
// course of theirs is painted with, and the colours each layout type starts with.
//
// ── Where this came from ────────────────────────────────────────────────────
// Omar, 2026-08-28, replacing every earlier WorkspaceBrand decision, then his
// Brand B decisions of 2026-10-20/21 (the wiki's Brand_design §2, D9–D38):
//
//   "the Colours card is only a palette: it helps the admin pick colours with
//    the picker on each layout; it decides no colour in the course; up to 15
//    colours; it can be empty" (D10)
//
// Four consequences that are easy to get wrong, so they are stated here:
//
//   1. NOTHING IS MANDATORY. The list may be EMPTY: the pickers then offer the
//      standard colours only, which is the client's choice.
//   2. EMPTY IS NOT THE SAME AS ABSENT. A brand with `colours: []` is a client
//      who removed every colour. A workspace with NO brand row at all has never
//      saved the screen, and gets FACTORY_WORKSPACE_BRAND. `resolveWorkspaceBrand`
//      is the one place that distinction is made
//      (`feedback_absence_and_emptiness_read_the_same`).
//   3. THE LIST DECIDES NO COLOUR. What learners see comes from the layouts'
//      colours and the seven Course-wide colours; there is no "Apply", and no
//      layout colour is worked out from the list any more (D9, D10).
//   4. AN OLD BRAND STILL OPENS. Brands saved before 2026-10-21 carry two lists,
//      `palette: { primary, accent }`. `normaliseWorkspaceBrand` turns them into
//      one, on read AND on save, so a tab still running the old screen saves
//      without failing. Anything an old brand lacks takes the starting values;
//      nothing keeps or changes an old workspace's look (D31). The one save it
//      cannot honour — an old tab that read the NEW shape and sends its two lists
//      beside it — is refused by the route, asking for a reload.
//
// ── What saving a brand DOES ────────────────────────────────────────────────
// LAYOUT colours: new layouts only (Omar, 2026-08-28). A layout created from now
// on is born carrying `layouts[<type>]`; layouts that already exist in saved
// courses are never touched.
//
// COURSE-WIDE colours: every course, at its next preview or export (D22), like
// the logo — they are painted by the export into the course's style sheet.
import { z } from "zod";
import { FACTORY_WORKSPACE_BRAND_LAYOUTS } from "./brand-factory-layouts.js";
import { ColorSchema } from "./shared.js";
/**
 * The 14 layout types a brand can carry a colour combination for — the same
 * set, and the same spelling, as `LayoutContentSchema`'s discriminator.
 *
 * ⚠️ Not derived from `LayoutContentSchema` at runtime on purpose: a
 * hand-written list is what lets `brand-colour-fields.test.ts` compare the two
 * and FAIL when they diverge. Deriving it would make that comparison read its
 * own output (`feedback_a_check_that_reads_its_own_output_is_circular`).
 */
export const WORKSPACE_BRAND_LAYOUT_TYPES = [
    "fullscreen_text_and_image",
    "title",
    "two_columns_text",
    "text_and_image",
    "small_video",
    "fullscreen_video",
    "sequence",
    "vertical_tabs",
    "icons_discover",
    "horizontal_tabs",
    "hidden_items",
    "object_viewer",
    "quiz_images",
    "quiz_gaming",
];
export const WorkspaceBrandLayoutTypeSchema = z.enum(WORKSPACE_BRAND_LAYOUT_TYPES);
/** How many colours a client may keep in the Colours list. Omar, 2026-10-20: "up to 15 colours". */
export const WORKSPACE_BRAND_COLOURS_MAX = 15;
/**
 * The seven Course-wide colours, in the order the screen shows them (D24: the prototype's
 * Course-wide card, plus "Accessibility icon when pointed at"). What each one paints in the
 * course player lives with the export (`@dynamo/scorm-packager`'s course-wide rules), and the
 * names the screen shows live in `brand-colour-fields.ts`.
 */
export const WORKSPACE_BRAND_COURSE_WIDE_KEYS = [
    "loadingBar",
    "progressBar",
    "questionPanel",
    "accessibilityIcon",
    "accessibilityIconHover",
    "homeButton",
    "homeButtonIcon",
];
/**
 * All seven, always. `.strict()` so a key nothing paints is refused rather than stored
 * (`feedback_bound_anything_that_gates_a_write`). A brand that names fewer is completed with
 * the starting values by `normaliseWorkspaceBrand` before this runs.
 */
export const WorkspaceBrandCourseWideSchema = z
    .object({
    loadingBar: ColorSchema,
    progressBar: ColorSchema,
    questionPanel: ColorSchema,
    accessibilityIcon: ColorSchema,
    accessibilityIconHover: ColorSchema,
    homeButton: ColorSchema,
    homeButtonIcon: ColorSchema,
})
    .strict();
/**
 * One layout type's colour combination: field path → colour.
 *
 * The KEYS are validated against `WORKSPACE_BRAND_COLOUR_FIELDS` by
 * `validateBrandLayoutFields` rather than by this schema, because the legal key
 * set differs per layout type and a `z.record` cannot express that. The route
 * runs both.
 */
export const WorkspaceBrandLayoutColoursSchema = z.record(z.string(), ColorSchema);
/**
 * An uploaded workspace logo, as `asset://<uuid>` — the same scheme every other
 * media field in this system uses, so the packager's existing asset resolution
 * applies unchanged (`feedback_one_door_per_field_reuse_existing_style`).
 */
export const WorkspaceBrandLogoRefSchema = z
    .string()
    .regex(/^asset:\/\/[0-9a-fA-F-]{36}$/, "the workspace logo must be an asset reference of the form asset://<uuid>");
const WorkspaceBrandShape = z.object({
    /** The client's colours, offered first in every picker. Up to 15, may be empty (D10). */
    colours: z.array(ColorSchema).max(WORKSPACE_BRAND_COLOURS_MAX),
    courseWide: WorkspaceBrandCourseWideSchema,
    /** Absent means "no logo uploaded" — the package then carries the Dynamo logo. */
    logo: WorkspaceBrandLogoRefSchema.optional(),
    /** Absent per type means "this layout type has no stored colours": the starting ones answer. */
    layouts: z.record(WorkspaceBrandLayoutTypeSchema, WorkspaceBrandLayoutColoursSchema).default({}),
});
/**
 * What every workspace starts from before it saves its brand — and so what a NEW client
 * starts from. Dynamo's five colours (D15), the Course-wide colours Omar set in the
 * prototype (D13; the pointed-at icon is B1's proposal, D24), and his colours on every
 * layout type (`brand-factory-layouts.ts`). No logo: the packager writes the Dynamo logo.
 */
export const FACTORY_WORKSPACE_BRAND = deepFreeze({
    colours: ["#000000", "#FFFFFF", "#F8F8F8", "#2D6396", "#97C3CB"],
    courseWide: {
        loadingBar: "#2D6396",
        progressBar: "#97C3CB",
        questionPanel: "#2D6396",
        accessibilityIcon: "#2D6396",
        accessibilityIconHover: "#97C3CB",
        homeButton: "#2D6396",
        homeButtonIcon: "#FFFFFF",
    },
    layouts: FACTORY_WORKSPACE_BRAND_LAYOUTS,
});
function deepFreeze(o) {
    if (o && typeof o === "object" && !Object.isFrozen(o)) {
        Object.freeze(o);
        for (const v of Object.values(o))
            deepFreeze(v);
    }
    return o;
}
const isPlainObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
/**
 * ONE answer to "what does this stored or sent brand mean today?" — used by the read
 * (`resolveWorkspaceBrand`) and by the save (`WorkspaceBrandSchema`), so the two cannot
 * disagree.
 *
 *   · An OLD brand's two lists become ONE: Primary then Accents, each colour once (case
 *     does not make a second colour), in order. Only when both are lists — anything else
 *     is left to fail the schema, so a damaged row still falls back loudly rather than
 *     quietly becoming an empty palette.
 *   · A new-shape `colours` sent beside an old `palette` wins. (A SAVE carrying both is refused by the route
 *     before this runs: only a tab still running the old screen sends that, and its change would be lost.)
 *   · Course-wide colours the brand does not name take the starting values. Course-wide colours that are not
 *     a set of colours at all are left as they are, for the schema to refuse — never replaced by the starting
 *     ones, which would store a damaged save as though somebody had chosen them.
 *
 * Returns its input unchanged when it is not an object, for the schema to refuse.
 */
export function normaliseWorkspaceBrand(raw) {
    if (!isPlainObject(raw))
        return raw;
    const { palette, ...rest } = raw;
    const out = { ...rest };
    if (!("colours" in raw) && isPlainObject(palette)
        && Array.isArray(palette.primary) && Array.isArray(palette.accent)) {
        const seen = new Set();
        out.colours = [...palette.primary, ...palette.accent].filter((c) => {
            if (typeof c !== "string")
                return true; // left for the schema to refuse
            const k = c.trim().toLowerCase();
            if (seen.has(k))
                return false;
            seen.add(k);
            return true;
        });
    }
    if (raw.courseWide === undefined || isPlainObject(raw.courseWide)) {
        out.courseWide = {
            ...FACTORY_WORKSPACE_BRAND.courseWide,
            ...(isPlainObject(raw.courseWide) ? raw.courseWide : {}),
        };
    }
    return out;
}
/**
 * A brand as it is saved: the new shape, or an old one, normalised first. The route runs
 * `unknownWorkspaceBrandColourPaths` on the layouts after this.
 */
export const WorkspaceBrandSchema = z.preprocess(normaliseWorkspaceBrand, WorkspaceBrandShape);
/**
 * The brand a workspace should be shown, and whether it is stored or factory.
 *
 * The second half of the return value is the point: the screen must be able to
 * say "these are the starting colours" rather than implying someone chose them
 * (`feedback_an_empty_control_hides_the_default_it_avoids`).
 */
export function resolveWorkspaceBrand(stored) {
    if (stored === null || stored === undefined) {
        return { brand: FACTORY_WORKSPACE_BRAND, source: "factory" };
    }
    const parsed = WorkspaceBrandSchema.safeParse(stored);
    if (!parsed.success) {
        // A stored row that no longer parses is a defect, not a reason to blank a
        // client's screen. Fall back loudly rather than throwing: the caller logs,
        // the client sees the starting colours, and nothing is overwritten until
        // they press Save.
        return { brand: FACTORY_WORKSPACE_BRAND, source: "factory" };
    }
    return { brand: parsed.data, source: "stored" };
}
//# sourceMappingURL=brand.js.map